import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Table } from 'react-bootstrap';
import PropTypes from 'prop-types';

// Components
import WeekSelectorModal from './WeekSelectorModal';
import DownloadReportButton from './DownloadReportButton';

// Actions
import { download_report, set_download_report_error } from '../../actions/reportsActions';

// Styles
import { tableStyles } from '../../styles/index';

// Utils
import { cycleDaysInfo } from '../../utils/datesHandler';

// Helper function to check if report is a random/aleatorio report
const isRandomReport = (report) => {
	const searchTerms = ['aleatorio', 'aleatorios', 'random'];
	const fieldsToCheck = [
		report?.alias?.toLowerCase() || '',
		report?.name?.toLowerCase() || '',
		report?.description?.toLowerCase() || ''
	];

	return fieldsToCheck.some(field =>
		searchTerms.some(term => field.includes(term))
	);
};

const TableData = (props) => {
	const { cycle, orgFilters, areaFilters, areaLabels, progressFilters, org_reports, showStaffData } = props;

	const dispatch = useDispatch();
	
	const { global_data } = useSelector(state => state.global);
	
	const { progress } = useSelector(state => state.overview);

	const { download_report_loading } = useSelector(state => state.reports);

	const [daysInfo, setDaysInfo] = useState({ workingDays: 0, currentDays: 0, remainingDays: 0 });

	// Week selector modal state
	const [showWeekModal, setShowWeekModal] = useState(false);
	const [selectedRandomReport, setSelectedRandomReport] = useState(null);

	// Toast for download started (GCP reports — no completion signal available)
	const [showToast, setShowToast] = useState(false);

	const triggerDownloadToast = () => {
		setShowToast(true);
		setTimeout(() => setShowToast(false), 5000);
	}

	useEffect(() => {
		const [workingDays, currentDays, remainingDays] = cycleDaysInfo(cycle.start.$date, cycle.end.$date);

		setDaysInfo({
			...daysInfo,
			workingDays: workingDays,
			currentDays: currentDays,
			remainingDays: remainingDays
		});
	}, [cycle]);

	const getDailyPayloads = (notWorked = 0) => {
		let censusPerDay = 0;
		
		if (cycle?.status !== 5) {
			if (daysInfo.remainingDays <= 0) censusPerDay = notWorked;
			else censusPerDay = notWorked / daysInfo.remainingDays;

			let floor = Math.floor(censusPerDay);
			if (floor * daysInfo.remainingDays >= notWorked) censusPerDay = floor;
			else censusPerDay = Math.ceil(censusPerDay);
		}
		
		return censusPerDay.toLocaleString(localStorage.getItem('i18nextLng'));
	}

	// Handle report button click
	const handleReportClick = (report) => {
		if (isRandomReport(report)) {
			setSelectedRandomReport(report);
			setShowWeekModal(true);
		} else {
			if (report.create_tab) triggerDownloadToast();
			dispatch(download_report(report, { orgFilters, areaFilters, areaLabels, progressFilters, showStaffData }));
		}
	};

	// Handle random report download with selected week
	const handleRandomReportDownload = (report, selectedWeek) => {
		const fileId = localStorage.getItem('latestFileInfo') ? JSON.parse(localStorage.getItem('latestFileInfo')).id : '';

		// If fileId is empty, dispatch error and close modal
		if (!fileId) {
			dispatch(set_download_report_error(report._id.$oid, 'Archivo de carga inválido'));
			setShowWeekModal(false);
			setSelectedRandomReport(null);
			return;
		}

		const weekFilters = {
			orgFilters: { ...orgFilters, file: fileId, week: selectedWeek.value },
			areaFilters,
			areaLabels,
			progressFilters,
			showStaffData
		};

		const filename = `${report.name.toLowerCase()}-${cycle.description.toLowerCase().replace(/ /g, '-')}-semana-${selectedWeek.value}.csv`;
		if (report.create_tab) triggerDownloadToast();
		dispatch(download_report(report, weekFilters, filename));
		setShowWeekModal(false);
		setSelectedRandomReport(null);
	};

	return (
		<div className='mt-5 mb-5'>
			<Table responsive>
				<thead className='align-middle text-center'>
					<tr style={{ height: '60px' }}>
						<th colSpan='5' style={tableStyles.issues}></th>
						<th colSpan='6' style={tableStyles.issuesHeader}>{global_data?.progressTable?.issuesHeader}</th>
						<th colSpan='8' style={tableStyles.issues}></th>
					</tr>
					<tr style={{ height: '105px' }}>
						<th style={tableStyles.cellText}>{global_data?.progressTable?.area}</th>
						<th style={tableStyles.cellText}>{global_data?.progressTable?.available}</th>
						<th style={tableStyles.cellText}>{global_data?.progressTable?.work}</th>
						<th style={tableStyles.percentage}>{global_data?.progressTable?.workPercentage}</th>
						<th style={tableStyles.cellText}>{global_data?.progressTable?.visits}</th>
						{/* Issues */}
						<th style={tableStyles.cellText}>{global_data?.progressTable?.issueClosed}</th>
						<th style={tableStyles.cellText}>{global_data?.progressTable?.issueClosedDown}</th>
						<th style={tableStyles.cellText}>{global_data?.progressTable?.issueAccess}</th>
						<th style={tableStyles.cellText}>{global_data?.progressTable?.issueProcess}</th>
						<th style={tableStyles.cellText}>{global_data?.progressTable?.issueProducts}</th>
						<th style={tableStyles.cellText}>{global_data?.progressTable?.issueCustom}</th>
						<th style={tableStyles.cellText}>{global_data?.progressTable?.issuesTotal}</th>
						<th style={tableStyles.percentage}>{global_data?.progressTable?.issuesPercentage}</th>
						{/* End Issues */}
						<th style={tableStyles.cellText}>{global_data?.progressTable?.notWorked}</th>
						<th style={tableStyles.percentage}>{global_data?.progressTable?.notWorkedPercentage}</th>
						<th style={tableStyles.cellText}>{global_data?.progressTable?.total}</th>
						<th style={tableStyles.percentage}>{global_data?.progressTable?.totalPercentage}</th>
						{cycle.status !== 5 &&
							<th style={tableStyles.cellText}>{global_data?.progressTable?.dailyPayloads}</th>
						}
						<th style={tableStyles.cellText}>{global_data?.progressTable?.scoreAverage}</th>
					</tr>
				</thead>
				<tbody className='align-middle text-center'>
					{progress?.data?.length === 0
						?	<tr style={{ height: '53px' }}>
								<td colSpan='17'>{global_data?.utils?.noData}</td>
							</tr>
						:	progress?.data.map((row, i) => (
								<tr key={i} style={{ backgroundColor: row?.name === 'TOTAL' ? '#e0e0e0': '#ffffff', height: '53px' }}>
									<td>{row?.name}</td>
									<td>{row?.available.toLocaleString(localStorage.getItem('i18nextLng'))}</td>
									<td>{row?.work.toLocaleString(localStorage.getItem('i18nextLng'))}</td>
									<td>
										{row?.available === 0 ? '0.00%' : ((row?.work / row?.available) * 100).toFixed(2) + '%'}
									</td>
									<td>{row?.visits ? row?.visits.toLocaleString(localStorage.getItem('i18nextLng')) : '-'}</td>
									<td>{row?.closed.toLocaleString(localStorage.getItem('i18nextLng'))}</td>
									<td>{row?.closed_down.toLocaleString(localStorage.getItem('i18nextLng'))}</td>
									<td>{row?.access.toLocaleString(localStorage.getItem('i18nextLng'))}</td>
									<td>{row?.process.toLocaleString(localStorage.getItem('i18nextLng'))}</td>
									<td>{row?.products.toLocaleString(localStorage.getItem('i18nextLng'))}</td>
									<td>{row?.custom.toLocaleString(localStorage.getItem('i18nextLng'))}</td>
									<td>{row?.issues.toLocaleString(localStorage.getItem('i18nextLng'))}</td>
									<td>
										{row?.available === 0 ? '0.00%' : ((row?.issues / row?.available) * 100).toFixed(2) + '%'}
									</td>
									<td>{(row?.available - (row?.work + row?.issues)).toLocaleString(localStorage.getItem('i18nextLng'))}</td>
									<td>
										{(row?.available - (row?.work + row?.issues)) / row?.available === 1 || row?.available === 0 ? '0.00%'
											: (((row?.available - (row?.work + row?.issues)) / row?.available) * 100).toFixed(2) + '%'
										}
									</td>
									<td>{(row?.work + row?.issues).toLocaleString(localStorage.getItem('i18nextLng'))}</td>
									<td>
										{row?.available === 0 ? '0.00%' : (((row?.work + row?.issues) / row?.available) * 100).toFixed(2) + '%'}
									</td>
									{cycle.status !== 5 &&
										<td>{getDailyPayloads(row?.available - (row?.work + row?.issues))}</td>
									}
									<td>{row?.average_score?.toFixed(2)?.toLocaleString(localStorage.getItem('i18nextLng')) || 0}</td>
								</tr>
							))
					}
				</tbody>
				{org_reports.length > 0 && 
					<tfoot style={{ backgroundColor: 'white' }}>
						<tr style={{ height: '70px' }}>
							<td colSpan='19'>
								{org_reports.map((report) => (
									<DownloadReportButton key={report._id.$oid} report={report} onClick={handleReportClick} />
								))}
							</td>
						</tr>
					</tfoot>
				}
			</Table>

			{/* Week Selector Modal for Random Reports */}
			<WeekSelectorModal
				show={showWeekModal}
				onHide={() => {
					setShowWeekModal(false);
					setSelectedRandomReport(null);
				}}
				cycle={cycle}
				report={selectedRandomReport}
				onDownload={handleRandomReportDownload}
				loading={download_report_loading.loading && download_report_loading.report_id === selectedRandomReport?._id?.$oid}
			/>

			<div className={`app-toast${showToast ? ' show' : ''}`}>
				<i className='bi bi-info-circle-fill' />
				<span>Descarga en proceso. Aparecerá en tu barra de descargas cuando esté lista.</span>
			</div>
		</div>
	);
}

TableData.propTypes = {
	cycle: PropTypes.object.isRequired,
	orgFilters: PropTypes.object.isRequired,
	areaFilters: PropTypes.object.isRequired,
	areaLabels: PropTypes.object.isRequired,
	progressFilters: PropTypes.object.isRequired,
	org_reports: PropTypes.array.isRequired,
	showStaffData: PropTypes.bool.isRequired
};

export default TableData;
